"use client"

import { useId, useState } from "react"
import type { LucideIcon } from "lucide-react"
import { CarFront, Delete, PhoneForwarded, Send, Users } from "lucide-react"

import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { TelephoneKeypad } from "@/components/telephone-keypad"
import { cn } from "@/lib/utils"

import {
  NowCallingPopoverHandle,
  keypadPanelWidthClass,
  nowCallingPopoverContentClass,
  nowCallingSurfaceClass,
} from "./now-calling-popover-handle"
import {
  formatTransferDialInput,
  transferDialToDigits,
} from "./transfer-dial-input"

type TransferTab = "destinations" | "dialpad"

type TransferDestinationKind = "extension" | "parking" | "conference"

type TransferDestination = {
  id: string
  kind: TransferDestinationKind
  label: string
  target: string
}

const transferDestinations: TransferDestination[] = [
  { id: "ext-100", kind: "extension", label: "Reception", target: "100" },
  { id: "ext-101", kind: "extension", label: "Jane Smith", target: "101" },
  { id: "ext-102", kind: "extension", label: "John Doe", target: "102" },
  { id: "ext-200", kind: "extension", label: "Sales Queue", target: "200" },
  { id: "ext-305", kind: "extension", label: "Support Team", target: "305" },
  { id: "park-701", kind: "parking", label: "Parking 701", target: "701" },
  { id: "park-702", kind: "parking", label: "Parking 702", target: "702" },
  { id: "park-703", kind: "parking", label: "Parking 703", target: "703" },
  { id: "conf-8001", kind: "conference", label: "Main Conference", target: "8001" },
  { id: "conf-8002", kind: "conference", label: "Huddle Room", target: "8002" },
  { id: "conf-8003", kind: "conference", label: "Board Room", target: "8003" },
]

const destinationKindMeta: Record<
  TransferDestinationKind,
  { label: string; icon: LucideIcon }
> = {
  extension: { label: "Extension", icon: PhoneForwarded },
  parking: { label: "Parking", icon: CarFront },
  conference: { label: "Conference", icon: Users },
}

function TransferTabBar({
  activeTab,
  onChange,
}: {
  activeTab: TransferTab
  onChange: (tab: TransferTab) => void
}) {
  return (
    <div
      className="-mx-3 mb-3 flex shrink-0 gap-0 border-b border-slate-300/80 px-3"
      role="tablist"
      aria-label="Transfer options"
    >
      {(
        [
          { id: "destinations" as const, label: "Destinations" },
          { id: "dialpad" as const, label: "Dialpad" },
        ] as const
      ).map((tab) => {
        const selected = activeTab === tab.id
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`transfer-panel-${tab.id}`}
            id={`transfer-tab-${tab.id}`}
            onClick={() => onChange(tab.id)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
              selected
                ? "border-blue-600 text-slate-900"
                : "border-transparent text-slate-500 hover:border-slate-300/90 hover:text-slate-700"
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

const destinationRowClass =
  "flex w-full items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-2 py-1 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"

const destinationSelectButtonClass =
  "flex min-w-0 flex-1 items-center gap-2 text-left"

const destinationSendButtonClass =
  "inline-flex shrink-0 items-center justify-center gap-0.5 rounded-sm border border-blue-300 bg-gradient-to-b from-sky-50 via-sky-100 to-blue-200 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(191_219_254/0.6)] transition-all hover:brightness-105 active:translate-y-px"

const transferSubmitButtonClass =
  "inline-flex items-center justify-center gap-1.5 rounded-sm border border-blue-300 bg-gradient-to-b from-sky-50 via-sky-100 to-blue-200 px-4 py-2.5 text-xs font-semibold text-blue-600 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(191_219_254/0.6)] transition-all hover:brightness-105 active:translate-y-px active:shadow-[inset_0_2px_4px_rgb(15_23_42/0.12)] disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none disabled:hover:brightness-100 disabled:active:translate-y-0"

function TransferDestinationsPanel({
  targetDigits,
  onSelect,
  onSend,
}: {
  targetDigits: string
  onSelect: (target: string) => void
  onSend: (target: string) => void
}) {
  return (
    <div
      id="transfer-panel-destinations"
      role="tabpanel"
      aria-labelledby="transfer-tab-destinations"
      className="flex h-full min-h-0 flex-col"
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-tl-md border border-slate-200/90 bg-white/80">
        <ul
          className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain p-1.5"
          aria-label="Transfer destinations"
        >
          {transferDestinations.map((destination) => {
            const meta = destinationKindMeta[destination.kind]
            const KindIcon = meta.icon
            const selected = targetDigits === destination.target

            return (
              <li key={destination.id}>
                <div
                  className={cn(
                    destinationRowClass,
                    selected &&
                      "border-blue-300 bg-blue-50/80 ring-1 ring-blue-200/80"
                  )}
                >
                  <button
                    type="button"
                    className={destinationSelectButtonClass}
                    onClick={() => onSelect(destination.target)}
                  >
                    <KindIcon
                      className="size-3.5 shrink-0 text-slate-500"
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-semibold text-slate-900">
                        {destination.label}
                      </span>
                      <span className="block text-[11px] text-slate-500">
                        {meta.label} · {destination.target}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={destinationSendButtonClass}
                    aria-label={`Send transfer to ${destination.label}`}
                    onClick={() => onSend(destination.target)}
                  >
                    <Send className="size-2.5 shrink-0" aria-hidden />
                    Send
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function TransferDialPadPanel({
  targetDigits,
  onTargetChange,
  inputId,
}: {
  targetDigits: string
  onTargetChange: (digits: string) => void
  inputId: string
}) {
  return (
    <div
      id="transfer-panel-dialpad"
      role="tabpanel"
      aria-labelledby="transfer-tab-dialpad"
      className="flex flex-col gap-3"
    >
      <div className="relative shrink-0">
        <label htmlFor={inputId} className="sr-only">
          Transfer to phone number or extension
        </label>
        <Input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder="Ext. or Phone Number"
          value={formatTransferDialInput(targetDigits)}
          onChange={(event) =>
            onTargetChange(transferDialToDigits(event.target.value))
          }
          className={cn(
            "h-10 rounded-sm border-slate-300 bg-white/90 text-center font-mono text-sm tabular-nums tracking-wide shadow-inner",
            "placeholder:text-slate-400",
            "focus-visible:border-slate-400 focus-visible:ring-slate-400/25"
          )}
        />
        {targetDigits.length > 0 ? (
          <button
            type="button"
            aria-label="Delete last digit"
            className={cn(
              "absolute right-1 top-1/2 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded-sm",
              "text-slate-500 transition-colors hover:bg-slate-200/90 hover:text-slate-900"
            )}
            onClick={() => {
              onTargetChange(targetDigits.slice(0, -1))
              document.getElementById(inputId)?.focus()
            }}
          >
            <Delete className="size-3.5" strokeWidth={2.25} aria-hidden />
          </button>
        ) : null}
      </div>
      <TelephoneKeypad
        ignoreSpecialKeys
        onDigitPress={(digit) =>
          onTargetChange(transferDialToDigits(targetDigits + digit))
        }
      />
    </div>
  )
}

function TransferActionRow({
  canSend,
  onSend,
}: {
  canSend: boolean
  onSend: () => void
}) {
  return (
    <div className="mt-1.5 flex shrink-0 justify-end border-t border-slate-300/80 pt-1.5">
      <button
        type="button"
        className={transferSubmitButtonClass}
        disabled={!canSend}
        onClick={onSend}
      >
        <PhoneForwarded className="size-3.5 shrink-0" aria-hidden />
        Send Transfer
      </button>
    </div>
  )
}

export function NowCallingTransferButton({
  action,
  actionButtonClass,
  disabled = false,
  onTransfer,
}: {
  action: { label: string; icon?: LucideIcon; className: string }
  actionButtonClass: string
  disabled?: boolean
  onTransfer?: (target: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<TransferTab>("destinations")
  const [targetDigits, setTargetDigits] = useState("")
  const inputId = useId()
  const Icon = action.icon
  const canSendTransfer = transferDestinations.some(
    (destination) => destination.target === targetDigits
  )
  const sendTransfer = (target = targetDigits) => {
    if (!target || disabled) return
    onTransfer?.(target)
    setOpen(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Transfer"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            actionButtonClass,
            action.className,
            disabled && "pointer-events-none opacity-40",
            open &&
              "ring-2 ring-blue-400/70 ring-offset-1 ring-offset-slate-100 brightness-105"
          )}
        >
          {Icon ? <Icon className="size-4" /> : null}
          {action.label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        side="top"
        sideOffset={8}
        collisionPadding={12}
        className={nowCallingPopoverContentClass}
      >
        <div className="relative">
          <div
            className={cn(
              keypadPanelWidthClass,
              "overflow-hidden rounded-l-xl border border-r-0 border-slate-300 px-3 pb-3 pt-1.5 shadow-[0_10px_38px_rgb(15_23_42/0.18)]",
              nowCallingSurfaceClass
            )}
          >
            <TransferTabBar activeTab={activeTab} onChange={setActiveTab} />
            <div className="relative min-w-0">
              <div
                aria-hidden={activeTab !== "dialpad"}
                className={cn(
                  activeTab !== "dialpad" && "invisible pointer-events-none"
                )}
              >
                <TransferDialPadPanel
                  targetDigits={targetDigits}
                  onTargetChange={setTargetDigits}
                  inputId={inputId}
                />
              </div>
              <div
                aria-hidden={activeTab !== "destinations"}
                className={cn(
                  "absolute inset-0 flex flex-col",
                  activeTab !== "destinations" && "invisible pointer-events-none"
                )}
              >
                <TransferDestinationsPanel
                  targetDigits={targetDigits}
                  onSelect={setTargetDigits}
                  onSend={sendTransfer}
                />
              </div>
            </div>
            <TransferActionRow canSend={canSendTransfer && !disabled} onSend={() => sendTransfer()} />
          </div>
          <NowCallingPopoverHandle
            closeLabel="Close transfer"
            onClose={() => setOpen(false)}
            className="absolute left-full top-0 h-full rounded-tr-xl rounded-br-xl border-y border-r border-slate-300"
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
